import { CalendarOff, TriangleAlert } from 'lucide-react'

import type { BusinessDay } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatBusinessDate } from '@/lib/datetime'

/**
 * Whether anything can post at all today, said once at the top of the sheet.
 *
 * `acceptsPostings` is sent by the ledger rather than derived from the status, because a screen
 * that guesses wrong here either blocks real work or invites forty postings the ledger will refuse
 * after the money has already been counted. A branch with no open day is the ordinary state at
 * seven in the morning, not a fault, so the panel says what to do rather than reading as an error.
 */
export function BusinessDayGate({ day }: { day: BusinessDay }) {
  if (day.acceptsPostings && day.businessDate !== null) {
    return null
  }

  if (day.status === 'NotOpened') {
    return (
      <Alert tone="warning">
        <CalendarOff aria-hidden />
        <div className="space-y-1">
          <AlertTitle>No business day is open at this branch</AlertTitle>
          <AlertDescription>
            A collection has no ledger date to land on until somebody opens the day, so nothing on
            this sheet can post. The sheet can still be keyed and checked — it simply cannot be
            committed.
            {day.lastClosedBusinessDate ? (
              <> The branch last traded on {formatBusinessDate(day.lastClosedBusinessDate)}.</>
            ) : null}
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="warning">
      <TriangleAlert aria-hidden />
      <div className="space-y-1">
        <AlertTitle>
          {day.businessDate === null
            ? 'This branch has no ledger date'
            : `${formatBusinessDate(day.businessDate)} is closing`}
        </AlertTitle>
        <AlertDescription>
          End-of-day checks are running and the ledger is not taking new postings. Key the sheet
          now if it helps, but commit it into tomorrow&apos;s day.
        </AlertDescription>
      </div>
    </Alert>
  )
}
